import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchLatestStationTradeEvents, fetchTradeDataByReferenceNumber } from "../api/trade";
import { isUserAuthorizedByServer } from "../api/userAccess";
import EmployeeTradeTable from "../components/EmployeeTradeTable";
import PageLoading from "../components/PageLoading";
import TradeFilterForm from "../components/TradeFilterForm";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import {
  selectAdminTradeData,
  updateDateTypeFilter,
  updateEndDateFilter,
  updateEndDateFilterErrors,
  updateSearchString,
  updateSearchStringErrors,
  updateStartDateFilter,
  updateStartDateFilterErrors,
  updateStatusFilters,
  updateTradeFoundByReferenceNumberSearch,
  updateTradeFoundByReferenceNumberSearchIsDialogOpen,
  updateTradeFoundByReferenceNumberSearchIsLoading,
  updateTradeTypeFilters,
} from "../slices/adminTrade/adminTradeSlice";
import { selectWorkgroupData } from "../slices/commonConfig/commonConfigSlice";
import { selectStationFilter, selectUserSession, selectWorkgroupFilters, updateStationFilter, updateWorkgroupFilters } from "../slices/userSession/userSessionSlice";
import { selectWebSocketEventData } from "../slices/webSocketEvent/webSocketEventSlice";
import { logout } from "../util/authentication";
import { getAdminStation, getMultiStationAdminStationList } from "../util/commonUtils";
import {
  ALL_ADMIN_STATUSES,
  ALL_TRADE_TYPES,
  BREADCRUMBS_TITLE,
  ERROR_MAX_PAYLOAD_LIMIT_MSG,
  HOME_PATH
} from "../util/constants";
import { deserializeDateFromRedux, serializeDateToRedux, set_time_to_end_of_day } from "../util/dateTimeFormatter";
import { tradeDataErrorCodeToErrorMessageMap } from "../util/errorMapper";
import {
  DEFAULT_TRADE_ADMIN_END_DATE_FILTER,
  DEFAULT_TRADE_ADMIN_START_DATE_FILTER,
  filterTrades,
} from "../util/filterTrades";
import { validateEndDateFilter, validateStartDateFilter } from "../util/inputValidation";
import { ADMIN_MOBILE_WARNING_MESSAGE } from "../util/messageConstants";
import { initializeSearchIndex, searchTrades } from "../util/searchTrades";
import { getScrubbedTradeFormEvent, getScrubbedTradeFormEvents } from "../util/tradeFormEventScrubber";
import { getTradeWorkgroups } from "../util/tradeUtils";
import { mergeWebSocketAdminTrades } from "../util/webSocketUtils";

const TradeAdministrationPage = (props) => {
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const workgroupFilters = useSelector(selectWorkgroupFilters);
  const adminTradeData = useSelector(selectAdminTradeData);
  const webSocketEventData = useSelector(selectWebSocketEventData);
  const workgroupData = useSelector(selectWorkgroupData);
  const dispatch = useDispatch();
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorObject, setErrorObject] = useState({});
  const [stationTrades, setStationTrades] = useState([]);
  const [filteredStationTrades, setFilteredStationTrades] = useState([]);
  const [searchedStationTrades, setSearchedStationTrades] = useState([]);
  const [isSearch, setIsSearch] = useState(false);

  // We need these Refs because changes to them are synchronous (as opposed to the state variables being async) and
  // knowing that, we can check the value of them upon getting a response back to ensure queries from changing the
  // table filters are not overwritten in the event a filter is changed in the middle of waiting for a response
  // See doesResponseQueryParamsMatchCurrentFilters and how it is used for more details.
  const stationFilterRef = useRef("");
  const dateTypeFilterRef = useRef("");
  const startDateFilterRef = useRef(null);
  const endDateFilterRef = useRef(null);
  const tempWebSocketDataRef = useRef({});
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  const tradeTableDesktopRef = useGridApiRef();
  const isAdminPageRef = useRef(true);
  const isApiSubscribedRef = useRef(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      if (adminTradeData.startDateFilter === null) {
        initializeFilterState();
      }
      stationFilterRef.current = stationFilter;
      if (adminTradeData?.startDateFilterErrors === "" && adminTradeData?.endDateFilterErrors === "") {
        updateStationTradeEventList(
          userSession.eid,
          stationFilter,
          adminTradeData.dateTypeFilter,
          getStartDateFilterSafely(),
          getEndDateFilterSafely()
        );
      } else {
        setIsLoading(false);
      }
    }
    return () => {
      // Cleaning up lingering Promise handling...
      isApiSubscribedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  useEffect(() => {
    tempWebSocketDataRef.current = webSocketEventData.tradeAdminEvents;
    if (Object.values(webSocketEventData.tradeAdminEvents).length > 0) {
      if (!isFirstLoad) {
        setStationTrades((prevState) => {
          return mergeWebSocketAdminTrades(webSocketEventData.tradeAdminEvents, prevState);
        });
        tempWebSocketDataRef.current = {};
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webSocketEventData.tradeAdminEvents]);

  useEffect(() => {
    let newFilteredTrades = filterTrades(
      stationTrades,
      adminTradeData.dateTypeFilter,
      getStartDateFilterSafely(),
      getEndDateFilterSafely(),
      adminTradeData.tradeTypeFilters,
      adminTradeData.statusFilters,
      workgroupFilters,
      isAdminPageRef.current
    );
    applyFilters(newFilteredTrades);
    if (adminTradeData.searchString !== "") {
      handleSearchSubmit(adminTradeData.searchString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationTrades]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  // We need this initialization since these fields are dependent on the stationFilter value
  const initializeFilterState = () => {
    dispatch(updateStartDateFilter(serializeDateToRedux(DEFAULT_TRADE_ADMIN_START_DATE_FILTER(stationFilter))));
    dispatch(updateEndDateFilter(serializeDateToRedux(DEFAULT_TRADE_ADMIN_END_DATE_FILTER(stationFilter))));
  };

  // These safe functions are used to be sure the default value is provided in the event the redux state is not available yet
  const getStartDateFilterSafely = () => {
    return adminTradeData.startDateFilter
      ? deserializeDateFromRedux(adminTradeData.startDateFilter)
      : DEFAULT_TRADE_ADMIN_START_DATE_FILTER(stationFilter);
  };

  const getEndDateFilterSafely = () => {
    return adminTradeData.endDateFilter
      ? deserializeDateFromRedux(adminTradeData.endDateFilter)
      : DEFAULT_TRADE_ADMIN_END_DATE_FILTER(stationFilter);
  };

  const updateStationTradeEventList = (eid, station, dateType, startDate, endDate) => {
    isApiSubscribedRef.current = true;
    setLoading(true);
    setIsError(false);
    setErrorObject({});
    dateTypeFilterRef.current = dateType;
    startDateFilterRef.current = startDate;
    endDateFilterRef.current = endDate;
    fetchLatestStationTradeEvents(eid, station, dateType, startDate, endDate)
      .then((res) => res.json())
      .then((result) => {
        if (isApiSubscribedRef.current && doesResponseQueryParamsMatchCurrentFilters(result.queryParams)) {
          if (result.message) {
            if (result.message === ERROR_MAX_PAYLOAD_LIMIT_MSG) {
              setIsError(true);
              setErrorObject(tradeDataErrorCodeToErrorMessageMap.tradeDataMaxPayloadError);
            } else {
              throw new Error("Unexpected error");
            }
          } else {
            let scrubbedTradeFormEvents = getScrubbedTradeFormEvents(result.tradeFormEvents);
            setStationTrades(mergeWebSocketAdminTrades(tempWebSocketDataRef.current, scrubbedTradeFormEvents));
            tempWebSocketDataRef.current = {};
            setIsLoading(false);
            setIsFirstLoad(false);
          }
        }
      })
      .catch((err) => {
        console.error("error happened!", err);
        setIsLoading(false);
        setIsError(true);
        setErrorObject(tradeDataErrorCodeToErrorMessageMap.unexpectedTradeDataError);
      });
  };

  const doesResponseQueryParamsMatchCurrentFilters = (responseQueryParams) => {
    return (
      responseQueryParams.station === stationFilterRef.current &&
      decodeURI(responseQueryParams.dateType) === dateTypeFilterRef.current &&
      responseQueryParams.startDate === startDateFilterRef.current.toMillis().toString() &&
      responseQueryParams.endDate === endDateFilterRef.current.toMillis().toString()
    );
  };

  const applySearch = (searchString, searchedTrades) => {
    if (searchString === "") {
      setIsSearch(false);
      setSearchedStationTrades(searchedTrades);
      applyTradeTableScroll();
      setLoading(false);
    } else {
      setIsSearch(true);
      setSearchedStationTrades(searchedTrades);
      applyTradeTableScroll();
      setLoading(false);
    }
  };

  const applyFilters = (filteredTrades) => {
    initializeSearchIndex(filteredTrades, isAdminPageRef.current);

    if (isSearch) {
      searchTrades(adminTradeData.searchString, applySearch, isAdminPageRef.current);
    }

    setFilteredStationTrades(filteredTrades);
  };

  const applyTradeTableScroll = () => {
    // This ref scrolls desktop trades to top
    const currentDesktop = tradeTableDesktopRef.current;
    if (currentDesktop !== null && currentDesktop.getRowModels && currentDesktop.getRowModels().size > 0) {
      currentDesktop.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
    }
  };

  const setLoading = (isLoading) => {
    if (!isFirstLoad) {
      setIsLoading(isLoading);
    }
  };

  const handleDateTypeFilterChange = (event) => {
    updateStationTradeEventList(
      userSession.eid,
      stationFilter,
      event.target.value,
      deserializeDateFromRedux(adminTradeData.startDateFilter),
      deserializeDateFromRedux(adminTradeData.endDateFilter)
    );
    dispatch(updateDateTypeFilter(event.target.value));
    applyTradeTableScroll();
  };

  const handleStartDateFilterChange = (date) => {
    let newStartDateFilterErrors = validateStartDateFilter(
      date,
      deserializeDateFromRedux(adminTradeData.endDateFilter),
      stationFilter
    );
    dispatch(updateStartDateFilter(serializeDateToRedux(date)));
    dispatch(updateStartDateFilterErrors(newStartDateFilterErrors));
    if (newStartDateFilterErrors === "") {
      updateStationTradeEventList(
        userSession.eid,
        stationFilter,
        adminTradeData.dateTypeFilter,
        date,
        deserializeDateFromRedux(adminTradeData.endDateFilter)
      );
      dispatch(updateEndDateFilterErrors(""));
      applyTradeTableScroll();
    }
  };

  const handleEndDateFilterChange = (event) => {
    let date = set_time_to_end_of_day(event);
    let newEndDateFilterErrors = validateEndDateFilter(
      date,
      deserializeDateFromRedux(adminTradeData.startDateFilter),
      stationFilter
    );
    dispatch(updateEndDateFilter(serializeDateToRedux(date)));
    dispatch(updateEndDateFilterErrors(newEndDateFilterErrors));
    if (newEndDateFilterErrors === "") {
      updateStationTradeEventList(
        userSession.eid,
        stationFilter,
        adminTradeData.dateTypeFilter,
        deserializeDateFromRedux(adminTradeData.startDateFilter),
        date
      );
      dispatch(updateStartDateFilterErrors(""));
      applyTradeTableScroll();
    }
  };

  const handleTradeTypeFilterChange = (event) => {
    let newTradeTypeFilterSelectedList = event.target.value;
    if (newTradeTypeFilterSelectedList.includes("Select All")) {
      if (isTradeTypeSelectAllChecked()) {
        newTradeTypeFilterSelectedList = [];
      } else {
        newTradeTypeFilterSelectedList = ALL_TRADE_TYPES;
      }
    }
    setLoading(true);
    let newFilteredTrades = filterTrades(
      stationTrades,
      adminTradeData.dateTypeFilter,
      deserializeDateFromRedux(adminTradeData.startDateFilter),
      deserializeDateFromRedux(adminTradeData.endDateFilter),
      newTradeTypeFilterSelectedList,
      adminTradeData.statusFilters,
      workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateTradeTypeFilters(newTradeTypeFilterSelectedList));
    applyFilters(newFilteredTrades);
    setLoading(false);
    applyTradeTableScroll();
  };

  const isTradeTypeSelectAllChecked = () => {
    return adminTradeData.tradeTypeFilters.length === ALL_TRADE_TYPES.length;
  };

  const isTradeTypeSelectAllIndeterminate = () => {
    return (
      adminTradeData.tradeTypeFilters.length !== ALL_TRADE_TYPES.length && adminTradeData.tradeTypeFilters.length !== 0
    );
  };

  const handleStatusFilterChange = (event) => {
    let newStatusFilterSelectedList = event.target.value;
    if (newStatusFilterSelectedList.includes("Select All")) {
      if (isStatusSelectAllChecked()) {
        newStatusFilterSelectedList = [];
      } else {
        newStatusFilterSelectedList = ALL_ADMIN_STATUSES;
      }
    }

    setLoading(true);
    let newFilteredTrades = filterTrades(
      stationTrades,
      adminTradeData.dateTypeFilter,
      deserializeDateFromRedux(adminTradeData.startDateFilter),
      deserializeDateFromRedux(adminTradeData.endDateFilter),
      adminTradeData.tradeTypeFilters,
      newStatusFilterSelectedList,
      workgroupFilters,
      isAdminPageRef.current
    );
    dispatch(updateStatusFilters(newStatusFilterSelectedList));
    applyFilters(newFilteredTrades);
    setLoading(false);
    applyTradeTableScroll();
  };

  const isStatusSelectAllChecked = () => {
    return adminTradeData.statusFilters.length === ALL_ADMIN_STATUSES.length;
  };

  const isStatusSelectAllIndeterminate = () => {
    return (
      adminTradeData.statusFilters.length !== ALL_ADMIN_STATUSES.length && adminTradeData.statusFilters.length !== 0
    );
  };

  const handleWorkgroupFilterChange = (event) => {
    let newWorkgroupFilterSelectedList = event.target.value;
    if (newWorkgroupFilterSelectedList.includes("Select All")) {
      if (isWorkgroupSelectAllChecked()) {
        newWorkgroupFilterSelectedList = [];
      } else {
        newWorkgroupFilterSelectedList = getTradeWorkgroups(Object.keys(workgroupData));
      }
    }
    setLoading(true);
    let newFilteredTrades = filterTrades(
      stationTrades,
      adminTradeData.dateTypeFilter,
      deserializeDateFromRedux(adminTradeData.startDateFilter),
      deserializeDateFromRedux(adminTradeData.endDateFilter),
      adminTradeData.tradeTypeFilters,
      adminTradeData.statusFilters,
      newWorkgroupFilterSelectedList,
      isAdminPageRef.current
    );
    dispatch(updateWorkgroupFilters(newWorkgroupFilterSelectedList));
    applyFilters(newFilteredTrades);
    setLoading(false);
    applyTradeTableScroll();
  };

  const isWorkgroupSelectAllIndeterminate = () => {
    return (
      workgroupFilters.length !== getTradeWorkgroups(Object.keys(workgroupData)).length &&
      workgroupFilters.length !== 0
    );
  };

  const isWorkgroupSelectAllChecked = () => {
    return workgroupFilters.length === getTradeWorkgroups(Object.keys(workgroupData)).length;
  };

  const handleStationFilterChange = (event) => {
    dispatch(updateStationFilter(event.target.value));
    tempWebSocketDataRef.current = {};
    stationFilterRef.current = event.target.value;
    setIsLoading(true);
    updateStationTradeEventList(
      userSession.eid,
      event.target.value,
      dateTypeFilterRef.current,
      startDateFilterRef.current,
      endDateFilterRef.current
    );
    applyTradeTableScroll();
  };

  const handleSearchChange = (event) => {
    dispatch(updateSearchString(event.target.value));
  };

  const handleSearchBlur = (event) => {
    if (event.target.value.trim() === "") {
      handleSearchSubmit("");
    }
  };

  const handleSearchEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit(adminTradeData.searchString);
    }
  };

  const handleSearchCancel = () => {
    dispatch(updateSearchString(""));
    handleSearchSubmit("");
  };

  const handleSearchSubmit = (newSearchString) => {
    dispatch(updateSearchStringErrors(""));
    const newSearchStringTrimmed = newSearchString.trim();
    if (
      !isNaN(parseInt(newSearchStringTrimmed)) &&
      parseInt(newSearchStringTrimmed) >= 10000000 &&
      parseInt(newSearchStringTrimmed) <= 99999999
    ) {
      dispatch(updateTradeFoundByReferenceNumberSearchIsLoading(true));
      fetchTradeDataByReferenceNumber(newSearchStringTrimmed, userSession.eid, stationFilter, isAdminPageRef.current)
        .then((res) => res.json())
        .then((result) => {
          if (result.message) {
            dispatch(updateSearchStringErrors(result.message));
            dispatch(updateTradeFoundByReferenceNumberSearchIsLoading(false));
          } else {
            let tradeFormEvent = result.latestTradeFormEvent;
            let scrubbedTradeFormEvent = getScrubbedTradeFormEvent(tradeFormEvent);
            dispatch(updateTradeFoundByReferenceNumberSearch(tradeFormEvent));
            dispatch(updateTradeFoundByReferenceNumberSearchIsLoading(false));
            applySearch(newSearchStringTrimmed, [scrubbedTradeFormEvent]);
            dispatch(updateTradeFoundByReferenceNumberSearchIsDialogOpen(true));
          }
        })
        .catch((err) => {
          console.error("error happened!", err);
          // TODO: This should display an error instead of closing the dialog for a better UX
          dispatch(updateTradeFoundByReferenceNumberSearchIsDialogOpen(false));
        });
    } else {
      searchTrades(newSearchStringTrimmed, applySearch, isAdminPageRef.current);
    }
  };

  const handleClickTradeFoundByReferenceNumberSearchDialogOpen = () => {
    dispatch(updateTradeFoundByReferenceNumberSearchIsDialogOpen(true));
  };

  const handleClickTradeFoundByReferenceNumberSearchDialogClose = () => {
    dispatch(updateTradeFoundByReferenceNumberSearchIsDialogOpen(false));
    dispatch(updateTradeFoundByReferenceNumberSearch(null));
  };
  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.TRADE_ADMINISTRATION];
  const breadcrumbsLink = [HOME_PATH];
  const renderDesktopTradeAdminPage = () => {
    return (
      <>
        <StarsBreadcrumbs
          isConfirmationRequired={false}
          breadcrumbsLinks={breadcrumbsLink}
          breadcrumbsList={breadcrumbsList}
        />
        <TradeFilterForm
          loggedInUserData={userSession}
          isAdminPage={isAdminPageRef.current}
          title="Trade Administration"
          trades={stationTrades}
          dateTypeFilter={adminTradeData.dateTypeFilter}
          onDateTypeFilterChange={handleDateTypeFilterChange}
          startDateFilter={getStartDateFilterSafely()}
          startDateFilterErrors={adminTradeData.startDateFilterErrors}
          onStartDateFilterChange={handleStartDateFilterChange}
          endDateFilter={getEndDateFilterSafely()}
          endDateFilterErrors={adminTradeData.endDateFilterErrors}
          onEndDateFilterChange={handleEndDateFilterChange}
          tradeTypeFilters={adminTradeData.tradeTypeFilters}
          allTradeTypeFilters={ALL_TRADE_TYPES}
          onTradeTypeFilterChange={handleTradeTypeFilterChange}
          isTradeTypeSelectAllChecked={isTradeTypeSelectAllChecked}
          isTradeTypeSelectAllIndeterminate={isTradeTypeSelectAllIndeterminate}
          statusFilters={adminTradeData.statusFilters}
          allStatusFilters={ALL_ADMIN_STATUSES}
          onStatusFilterChange={handleStatusFilterChange}
          isStatusSelectAllChecked={isStatusSelectAllChecked}
          isStatusSelectAllIndeterminate={isStatusSelectAllIndeterminate}
          workgroupFilters={getTradeWorkgroups(workgroupFilters)}
          allWorkgroupFilters={getTradeWorkgroups(Object.keys(workgroupData))}
          onWorkgroupFilterChange={handleWorkgroupFilterChange}
          isWorkgroupSelectAllChecked={isWorkgroupSelectAllChecked}
          isWorkgroupSelectAllIndeterminate={isWorkgroupSelectAllIndeterminate}
          stations={getMultiStationAdminStationList(userSession)}
          featureConfig={props.featureConfig}
          stationFilter={getAdminStation(props.featureConfig.userStations, userSession, stationFilter)}
          onStationFilterChange={handleStationFilterChange}
          searchString={adminTradeData.searchString}
          searchStringErrors={adminTradeData.searchStringErrors}
          tradeFoundByReferenceNumberSearch={
            adminTradeData.tradeFoundByReferenceNumberSearch
              ? getScrubbedTradeFormEvent(adminTradeData.tradeFoundByReferenceNumberSearch)
              : adminTradeData.tradeFoundByReferenceNumberSearch
          }
          tradeFoundByReferenceNumberSearchIsLoading={adminTradeData.tradeFoundByReferenceNumberSearchIsLoading}
          tradeFoundByReferenceNumberSearchIsDialogOpen={adminTradeData.tradeFoundByReferenceNumberSearchIsDialogOpen}
          onClickTradeFoundByReferenceNumberSearchDialogOpen={handleClickTradeFoundByReferenceNumberSearchDialogOpen}
          onClickTradeFoundByReferenceNumberSearchDialogClose={handleClickTradeFoundByReferenceNumberSearchDialogClose}
          onSearchChange={handleSearchChange}
          onSearchBlur={handleSearchBlur}
          onSearchEnterKeyPressed={handleSearchEnterKeyPressed}
          onSearchCancel={handleSearchCancel}
          onSearchSubmit={handleSearchSubmit}
          isDesktopView={props.isDesktopView}
        />

        <EmployeeTradeTable
          loggedInUserData={userSession}
          employeeTrades={isSearch ? searchedStationTrades : filteredStationTrades}
          station={stationFilterRef.current}
          isAdminPage={true}
          isFirstLoad={isFirstLoad}
          isLoading={isLoading}
          isError={isError}
          errorObject={errorObject}
          tradeTableDesktopRef={tradeTableDesktopRef}
          isDesktopView={props.isDesktopView}
        />
      </>
    );
  };

  const renderMobileTradeAdminPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }

  return (
    <div className="App-contents" data-testid="admin-trades-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopTradeAdminPage() : renderMobileTradeAdminPage()}</>
      )}
    </div>
  );
};

export default TradeAdministrationPage;
